'use client';
import React, { useEffect, useState } from 'react';
import { Zap, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

interface BreakingPost {
  id: string;
  title: string;
  slug: string;
}

export default function BreakingNewsBar() {
  const router = useRouter();
  const [headlines, setHeadlines] = useState<BreakingPost[]>([]);

  useEffect(() => {
    const supabase = createClient();

    const fetchHeadlines = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('id, title, slug')
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(20);
      if (data && data.length > 0) setHeadlines(data);
    };

    fetchHeadlines();

    const channel = supabase
      .channel('breaking_news_ticker')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'blog_posts' }, fetchHeadlines)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const displayHeadlines = headlines.length > 0 ? headlines : [
    { id: 'bn-001', title: 'Bitcoin Hits New All-Time High Above $67,000', slug: 'bitcoin-hits-new-all-time-high-above-67000' },
    { id: 'bn-002', title: "Ethereum Upgrade 'Dencun' Successfully Deployed", slug: 'ethereum-upgrade-dencun-successfully-deployed' },
    { id: 'bn-003', title: 'DeFi TVL Reaches $120B, Highest Since 2022', slug: 'defi-tvl-reaches-120b-highest-since-2022' },
    { id: 'bn-004', title: 'SEC Approves Spot Ethereum ETF Applications', slug: 'sec-approves-spot-ethereum-etf-applications' },
    { id: 'bn-005', title: 'Solana Network Processes Record 65,000 TPS', slug: 'solana-network-processes-record-65000-tps' },
    { id: 'bn-006', title: 'BlackRock Bitcoin ETF Surpasses $20B in AUM', slug: 'blackrock-bitcoin-etf-surpasses-20b-aum' },
  ];

  const handleHeadlineClick = (slug: string) => {
    router.push(`/blog/${slug}`);
  };

  return (
    <div className="w-full bg-black border-b border-border overflow-hidden">
      <div className="flex items-center h-10">
        <div className="flex-shrink-0 flex items-center gap-2 px-3 bg-primary h-full">
          <Zap size={13} className="text-white fill-white" />
          <span className="text-[11px] font-bold text-white uppercase tracking-wider whitespace-nowrap">
            Breaking News
          </span>
        </div>

        <div className="flex-1 overflow-hidden relative group">
          <div
            className="flex items-center gap-0 breaking-scroll group-hover:[animation-play-state:paused]"
            style={{ width: 'max-content' }}
          >
            {[...displayHeadlines, ...displayHeadlines]?.map((h, i) => (
              <button
                key={`bh-${h?.id}-${i}`}
                onClick={() => handleHeadlineClick(h.slug)}
                className="flex items-center gap-3 px-6 h-10 whitespace-nowrap cursor-pointer hover:text-primary transition-colors duration-150 bg-transparent border-none outline-none"
              >
                <span className="text-sm text-foreground font-medium hover:text-primary transition-colors duration-150">
                  {h?.title}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>

        <Link
          href="/blog"
          className="flex-shrink-0 flex items-center gap-1.5 px-4 text-primary text-[12px] font-semibold hover:text-primary/80 transition-colors duration-150 whitespace-nowrap border-l border-border h-full"
        >
          View All News <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}